import * as React from 'react';
import {
  StackLayout,
  StackItem,
  ParagraphText,
  type GDSHeadingLevel,
  type GDSSpaceToken,
  spacingTokenMap,
  visuallyHiddenStyle,
  type GDSSpace,
} from '@leagueplatform/genesis-core';
import {
  type MasonryEngineNode,
  type MasonryEngineNodeRendererProps,
} from '@leagueplatform/masonry-engine';
import { MasonryDriverAction } from '#types/masonry-driver-node-actions.ts';
import { SectionHeader } from '../masonry-section-header-renderer/section-header.component';
import { MasonryTextAction } from '../common/masonry-text-action/masonry-text-action.component';
import { formatClassNames } from '../../utils/format-masonry-class-name.util';

export interface MasonryVerticalLayoutNodeProperties {
  body?: string;
  headerButtonLabel?: string;
  heading?: string;
  headingLevel?: GDSHeadingLevel;
  isSemanticList?: boolean;
  spacing?: GDSSpaceToken;
  sectionHeaderPaddingTop?: GDSSpace;
}

export type MasonryVerticalLayoutNodeActions = {
  onHeaderButtonClick?: MasonryDriverAction;
};

export type MasonryVerticalLayoutNode = MasonryEngineNode<
  'verticalLayout',
  MasonryVerticalLayoutNodeProperties,
  MasonryVerticalLayoutNodeActions,
  {
    items: MasonryEngineNode<string, any>[];
  }
>;

export type MasonryVerticalLayoutRendererProps =
  MasonryEngineNodeRendererProps<MasonryVerticalLayoutNode>;

export const MasonryVerticalLayoutRenderer = ({
  body,
  headerButtonLabel,
  heading,
  headingLevel,
  isSemanticList,
  items,
  onHeaderButtonClick,
  spacing = 'one',
  rootElementRef,
  className,
  engineClassName,
  locatorId,
  css,
  sectionHeaderPaddingTop,
}: MasonryVerticalLayoutRendererProps) => {
  const hasTextAction: boolean = !!onHeaderButtonClick && !!headerButtonLabel;

  if (!items) return null;
  const layoutSpacing = spacingTokenMap[spacing];

  return (
    <StackLayout
      spacing="$one"
      horizontalAlignment="stretch"
      orientation="vertical"
      ref={rootElementRef}
      className={formatClassNames(engineClassName, className)}
      data-testid={locatorId}
      css={css}
    >
      {heading && (
        <SectionHeader
          headingLevel={headingLevel}
          heading={heading}
          paddingTop={sectionHeaderPaddingTop}
          textAction={
            hasTextAction ? (
              <MasonryTextAction
                onClick={onHeaderButtonClick}
                text={headerButtonLabel}
              />
            ) : undefined
          }
        />
      )}
      {body && <ParagraphText>{body}</ParagraphText>}
      {items && (
        <StackLayout
          as={isSemanticList ? 'ul' : 'div'}
          spacing={layoutSpacing ?? '$one'}
          horizontalAlignment="stretch"
          orientation="vertical"
        >
          {React.Children.map(
            items,
            (item) =>
              item && (
                <StackItem
                  as={isSemanticList ? 'li' : 'div'}
                  verticalAlignment="bottom"
                  css={{
                    '&:empty': {
                      display: 'none',
                    },
                    '&:has(> .visually-hidden)': {
                      ...visuallyHiddenStyle,
                    },
                  }}
                >
                  {item}
                </StackItem>
              ),
          )}
        </StackLayout>
      )}
    </StackLayout>
  );
};
